import React, {useEffect, useMemo} from "react";
import clsx from "clsx";
import {FaExclamationTriangle, FaMap} from "react-icons/fa";
import {FaMapLocation} from "react-icons/fa6";

const BUTTON_VARIANT_CLASS = {
    primary: "btn-primary",
    danger: "btn-danger",
    warning: "btn-warning",
};

const MENU_WIDTH = 190;
const MENU_ITEM_HEIGHT = 32;
const VIEWPORT_MARGIN = 8;

const getButtonClassName = action => clsx(
    "btn btn-xs",
    action.className || BUTTON_VARIANT_CLASS[action.variant] || BUTTON_VARIANT_CLASS.primary
);

const getMenuItemClassName = action => {
    const variant = action.menuVariant || action.variant;

    return clsx(
        "panel-context-menu-item",
        variant === "danger" && "panel-context-menu-item--danger",
        variant === "warning" && "panel-context-menu-item--warning",
    );
};

const runAction = (event, action, onAfterClick) => {
    event.preventDefault();
    event.stopPropagation();
    action.onClick?.();
    onAfterClick?.();
};

export const DashboardCardActionBar = ({card, actions}) => {
    if (!card || actions.length === 0) return null;

    return (
        <div
            className={clsx(
                card.type === "garrison" ? "garrisonActionsButton" : "attendOrTaskActionsButton",
                card.type === "garrison" ? "" : "mb-1.25",
            )}
            data-dashboard-card-actions="true"
            onMouseDown={event => event.stopPropagation()}
        >
                {actions.map(action => (
                    <button
                        key={action.key}
                        type="button"
                        className={getButtonClassName(action)}
                        onClick={event => runAction(event, action)}
                    >
                        {action.key === "attendance-map" && (<FaMapLocation />)}
                        {!["attendance-map"].includes(action.key) && (<b>{action.label}</b>)}
                    </button>
                ))}
        </div>
    );
};

export const DashboardCardContextMenu = ({menu, actions, onClose}) => {
    useEffect(() => {
        if (!menu) return undefined;

        const close = () => onClose();
        const closeOnEscape = event => {
            if (event.key === "Escape") onClose();
        };

        window.addEventListener("mousedown", close);
        window.addEventListener("keydown", closeOnEscape);
        window.addEventListener("scroll", close, true);

        return () => {
            window.removeEventListener("mousedown", close);
            window.removeEventListener("keydown", closeOnEscape);
            window.removeEventListener("scroll", close, true);
        };
    }, [menu, onClose]);

    const position = useMemo(() => {
        if (!menu || typeof window === "undefined") return {left: 0, top: 0};

        const menuHeight = Math.min(actions.length * MENU_ITEM_HEIGHT + VIEWPORT_MARGIN, window.innerHeight - (VIEWPORT_MARGIN * 2));
        const left = Math.min(menu.x, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN);
        const top = Math.min(menu.y, window.innerHeight - menuHeight - VIEWPORT_MARGIN);

        return {
            left: Math.max(VIEWPORT_MARGIN, left),
            top: Math.max(VIEWPORT_MARGIN, top),
        };
    }, [actions.length, menu]);

    if (!menu || actions.length === 0) return null;

    return (
        <div
            className="panel-context-menu"
            data-dashboard-card-actions="true"
            style={position}
            onMouseDown={event => event.stopPropagation()}
            onContextMenu={event => event.preventDefault()}
        >
            {actions.map(action => (
                <button
                    key={action.key}
                    type="button"
                    className={getMenuItemClassName(action)}
                    onClick={event => runAction(event, action, onClose)}
                >
                    {action.label}
                </button>
            ))}
        </div>
    );
};
